import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faAngleLeft, faAngleRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../common/formfields/CustomSelect";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const NewsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    const employeeCode = storageService.getItem("employeeCode");

    const [formData, setFormData] = useState({
        siteId: '',
        unitId: 1, // Defaulting to 1 based on usual tenant apps
        title: '',
        subTitle: '',
        navigationLink: '',
        backgroundColor: '',
        textColor: '',
        image: '',
        createdBy: employeeCode || '',
        isActiveId: 1,
        description: ''
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Get Site ID
                let currentSiteId = null;
                if (employeeCode) {
                    const userRes = await apiServices({
                        url: '/ResidentialUser/GetResidentialUserById',
                        method: 'POST',
                        data: { userId: employeeCode }
                    });
                    if (userRes.success && userRes.data?.data?.[0]) {
                        currentSiteId = userRes.data.data[0].siteId;
                        setFormData(prev => ({ ...prev, siteId: currentSiteId }));
                    }
                }

                // If edit mode, fetch news data
                if (isEditMode) {
                    const res = await apiServices({
                        url: `/ResidentialMarketingNews/GetMarketingNewsById/${id}`,
                        method: 'GET'
                    });

                    if (res.success && res.data?.Marketingnews?.length) {
                        const data = res.data.Marketingnews[0];
                        setFormData({
                            siteId: data.SiteId || currentSiteId,
                            unitId: data.UnitId || 1,
                            title: data.Title || '',
                            subTitle: data.SubTitle || '',
                            navigationLink: data.NavigationLink || '',
                            backgroundColor: data.BackgroundColor || '',
                            textColor: data.TextColor || '',
                            image: data.Image || '',
                            createdBy: data.CreatedBy || employeeCode,
                            isActiveId: data.IsActive === 'Active' ? 1 : 0,
                            description: data.Description || ''
                        });
                    } else {
                        toast.error("Failed to load newsletter details.");
                        navigate("/zentres/News-Letters");
                    }
                }
            } catch (error) {
                console.error("Error loading form data:", error);
                toast.error("Failed to load form data.");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [id, isEditMode, employeeCode, navigate]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (id, option) => {
        const val = option?.value;
        setFormData(prev => ({ ...prev, [id]: val }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormData(prev => ({ 
                    ...prev, 
                    image: reader.result,
                    backgroundColor: '' // Clear color if image is uploaded
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            toast.error("Please provide a Title.");
            return;
        }

        if (!formData.siteId) {
            toast.error("Site ID is missing. Please reload the page.");
            return;
        }

        setSubmitting(true);
        try {
            const url = isEditMode 
                ? `/ResidentialMarketingNews/UpdateMarketingNews/${id}`
                : `/ResidentialMarketingNews/postMarketingNews`;
            
            const res = await apiServices({
                url,
                method: isEditMode ? 'PUT' : 'POST',
                data: formData
            });

            if (res.success && res.data?.IsSuccess) {
                toast.success(res.data.Message || `Newsletter ${isEditMode ? 'updated' : 'created'} successfully!`);
                navigate("/zentres/News-Letters");
            } else {
                toast.error(res.data?.Message || "Failed to save newsletter.");
            }
        } catch (error) {
            console.error("Error saving newsletter:", error);
            toast.error("An error occurred while saving.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full w-full bg-white rounded-lg">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500 text-3xl" />
            </div>
        );
    }

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEditMode ? 'Edit' : 'Add'} Newsletter`}
                subHeading={`Please fill out the form below to ${isEditMode ? 'update the existing' : 'create a new'} newsletter.`}
            />

            <div className="mt-6 w-full max-w-5xl">
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 flex flex-col gap-6 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Title */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="title">Title <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Enter title"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                                required
                            />
                        </div>

                        {/* Sub Title */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="subTitle">Sub Title</label>
                            <input
                                type="text"
                                id="subTitle"
                                value={formData.subTitle}
                                onChange={handleInputChange}
                                placeholder="Enter sub title"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                            />
                        </div>

                        {/* Navigation Link */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="navigationLink">Navigation Link</label>
                            <input
                                type="text"
                                id="navigationLink"
                                value={formData.navigationLink}
                                onChange={handleInputChange}
                                placeholder="https://example.com"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                            />
                        </div>

                        {/* Background Color */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="backgroundColor">Background Color</label>
                            <input
                                type="color"
                                id="backgroundColor"
                                value={formData.backgroundColor || '#ffffff'}
                                onChange={(e) => setFormData(prev => ({ ...prev, backgroundColor: e.target.value, image: '' }))}
                                className="w-full h-[46px] p-1 bg-white border border-gray-200 rounded-xl cursor-pointer"
                            />
                        </div>

                        {/* Text Color */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="textColor">Text Color</label>
                            <input
                                type="color"
                                id="textColor"
                                value={formData.textColor || '#000000'}
                                onChange={handleInputChange}
                                className="w-full h-[46px] p-1 bg-white border border-gray-200 rounded-xl cursor-pointer"
                            />
                        </div>

                        {/* Status */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Status <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Active', value: 1 },
                                    { label: 'Inactive', value: 0 }
                                ]}
                                value={{ label: formData.isActiveId === 1 ? 'Active' : 'Inactive', value: formData.isActiveId }}
                                onChange={(opt) => handleSelectChange("isActiveId", opt)}
                                placeholder="Select Status"
                            />
                        </div>

                        {/* Image */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Newsletter Image</label>
                            <div className="relative group cursor-pointer w-full h-40 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors bg-white flex items-center justify-center overflow-hidden">
                                {formData.image ? (
                                    <img src={formData.image} alt="Preview" className="w-full h-full object-contain p-2" />
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                                        <FontAwesomeIcon icon={faCamera} className="text-3xl mb-2" />
                                        <span className="text-xs font-bold text-center px-4 uppercase tracking-wide">Upload Photo</span>
                                        <span className="text-[10px] mt-1 text-gray-400">Supported formats: JPG, PNG, GIF</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="description">Description</label>
                            <textarea
                                id="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Enter description"
                                rows="4"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 resize-none"
                            />
                        </div>

                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-between items-center mt-6 pt-6 mb-8">
                    <button
                        type="button"
                        onClick={() => navigate("/zentres/News-Letters")}
                        className="px-6 py-2.5 border border-gray-200 rounded-xl text-xs font-bold flex items-center gap-2 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-8 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }
                        }}
                    >
                        {submitting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEditMode ? 'Update Newsletter' : 'Save Newsletter')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewsForm;
